import React, { useState, useEffect, useRef } from'react'
import { useNavigate, useSearchParams } from'react-router-dom'
import { useProvideFeedbackContext } from'../components/feedback/FeedbackContext'
import { Button } from'../components/ui/Button'
import { User, Check, CheckCircle, AlertCircle, Loader2, Shield, ShieldAlert, ShieldCheck, Wrench, Trash2, RefreshCw, ChevronDown, ChevronRight, AlertTriangle, Database, Clock, Zap, Sun, Moon, Coffee, Star, Sparkles, Palette, Bell, Camera, Lock, Monitor, Smartphone, Tablet, Key, LogOut } from'lucide-react'
import { profileService, type UserProfile } from'../services/profileService'
import { securityService, type SecurityDashboard as SecurityData } from'../services/securityService'
import { formatDistanceToNow } from'date-fns'
import { useTheme, DIARY_FONTS, ACCENT_COLORS, UI_FONTS, THEMES, type Theme } from'../components/providers/ThemeProvider'
import { WithYouSettings } from'../components/with-you/WithYouSettings'
import { LifeAreaBudgetsEditor } from'../components/prioritization/LifeAreaBudgetsEditor'
import { getReminderSettings, setReminderDefault, setCriticalQuietMode } from'../services/reminderService'
import { BRAND } from'../config/brand'
import { SettingRow, SettingSwitch, SettingGroup, ASSUMED_NOTE } from'../components/settings/SettingRow'
import {
  DAY_KEYS, clockWord, minutesWord, countWord, daysWord, horizonWord,
  carryoverWord, toneWord, carryInWord, recoveryWord, atATimeWord, MOTIVATION_STYLES,
} from'../utils/settingsWords'

interface ItemCounts {
 dreams: number
 aspirations: number
 goals: number
 tasks: number
 action_items: number
 roadblocks: number
 knowledge_skills: number
}

/** Safe text for dev-tools deleted counts (v5 engine returns a nested object under horton_chat_v5). */
function formatDeletedCountFragment(value: unknown): string {
  if (value == null) return '0'
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return '[object]'
    }
  }
  return String(value)
}

interface MFAStatus {
 mfa_enabled: boolean
 has_backup_codes: boolean
}

interface SchedulingPreferences {
 // ── The shape of a day. Every one of these is read by a live scheduler;
 //    the four marked (wired 2026-09-06) were settable and read by nothing
 //    until the profile audit — see docs/PROFILE_UX_PASS_2026-09-06.md.
 wake_time: string
 sleep_time: string
 work_start_time: string
 work_end_time: string
 work_days: string[]                 // (wired 2026-09-06) no work slots on a day off
 energy_levels: Record<string, number>
 focus_duration_minutes: number      // (wired 2026-09-06) the floor for a work slot
 break_duration_minutes: number      // (wired 2026-09-06) reported with every slot
 buffer_between_tasks: number        // (wired 2026-09-06) padding around commitments
 lunch_start_time: string
 lunch_duration_minutes: number
 primary_motivation: string          // one line of the scheduler's context
 // ── How much of a day to fill
 daily_focus_limit: number
 daily_hold_limit: number
 max_parallel_tasks: number
 default_horizon: string
 auto_backfill_focus: boolean
 show_recurring_in_today: boolean
 recurring_counts_toward_focus: boolean
 enable_low_momentum_mode: boolean
 // (wired 2026-09-07) three more the orchestrator has always read while
 // nothing wrote them, so every person ran on the constant in the reader.
 low_momentum_focus_limit: number
 allow_carryover_incremental: boolean
 max_incremental_in_focus: number
}

/** The keys the Settings page still renders — the filter for both directions. */
const schedPrefsShape: Record<keyof SchedulingPreferences, true> = {
  wake_time: true, sleep_time: true, work_start_time: true, work_end_time: true,
  work_days: true, energy_levels: true, focus_duration_minutes: true,
  break_duration_minutes: true, buffer_between_tasks: true,
  lunch_start_time: true, lunch_duration_minutes: true, primary_motivation: true,
  daily_focus_limit: true, daily_hold_limit: true, max_parallel_tasks: true,
  default_horizon: true, auto_backfill_focus: true, show_recurring_in_today: true,
  recurring_counts_toward_focus: true, enable_low_momentum_mode: true,
  low_momentum_focus_limit: true, allow_carryover_incremental: true,
  max_incremental_in_focus: true,
}

/**
 * Four rooms. Each name says what it decides, so nobody has to guess which
 * branch holds a setting. Before 2026-09-06 there were five, two of them with
 * nested sub-tabs, and one ('horton') held a persona picker that changed
 * nothing at all.
 */
type ProfileTab = 'profile' | 'day' | 'attention' | 'security'

const TABS: { id: ProfileTab; label: string; icon: typeof User }[] = [
  { id: 'profile',   label: 'You',          icon: User },
  { id: 'day',       label: 'Your day',     icon: Clock },
  { id: 'attention', label: 'Reaching you', icon: Bell },
  { id: 'security',  label: 'Account',      icon: Shield },
]

// Personal Info interface
interface PersonalInfo {
 first_name: string
 last_name: string
 display_name: string
 title: string
 bio: string
 avatar_url: string
 timezone: string
 country: string
 city: string
}

// App Preferences interface
interface AppPreferences {
 theme_preference: string
 carryover_entry_mode: string
 /** The motivator's tone. Top-level on the profile, not nested. */
 motivation_style: string
}

/** One control style for every popover body, so the page looks like one page. */
const fieldCls =
  'w-full rounded-lg border border-soft bg-surface px-3 py-2 text-app-body text-primary focus:border-brand focus:outline-none'

const TIMEZONES = [
'UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
'America/Toronto','Europe/London','Europe/Paris','Europe/Berlin','Asia/Tokyo',
'Asia/Shanghai','Asia/Singapore','Asia/Dubai','Australia/Sydney','Pacific/Auckland'
]

// DiceBear avatar styles and seeds for variety
const DICEBEAR_STYLES = [
 { id:'avataaars', name:'Avataaars' },
 { id:'bottts', name:'Robots' },
 { id:'lorelei', name:'Lorelei' },
 { id:'notionists', name:'Notionists' },
 { id:'open-peeps', name:'Open Peeps' },
 { id:'personas', name:'Personas' },
 { id:'pixel-art', name:'Pixel Art' },
 { id:'adventurer', name:'Adventurer' },
 { id:'big-ears', name:'Big Ears' },
 { id:'micah', name:'Micah' },
]

// Animation styles for avatars
const ANIMATED_AVATAR_STYLES = [
 { id:'bounce', name:'🎾 Bounce' },
 { id:'pulse', name:'💓 Pulse' },
 { id:'spin', name:'🌀 Spin' },
 { id:'wiggle', name:'👋 Wiggle' },
 { id:'float', name:'🎈 Float' },
 { id:'jello', name:'🍮 Jello' },
]

// Fun animated avatar styles from DiceBear (best for animation)
const ANIMATED_DICEBEAR_STYLES = [
 { id:'bottts', name:'🤖 Robots' },
 { id:'fun-emoji', name:'😄 Fun Emoji' },
 { id:'thumbs', name:'👍 Thumbs' },
 { id:'icons', name:'⭐ Icons' },
 { id:'shapes', name:'🔷 Shapes' },
]

// Generate avatar seeds for variety
const AVATAR_SEEDS = [
'happy','sunny','cosmic','zen','spark','flow','dream','bright',
'calm','joy','star','moon','wave','forest','river','mountain',
'breeze','glow','harmony','peace','inspire','wonder','magic','light'
]

// More playful seeds for animated avatars
const ANIMATED_AVATAR_SEEDS = [
'party','dance','celebrate','energy','vibe','groove','fun','play',
'bounce','sparkle','twinkle','glow','shine','radiant','lively','peppy',
'zippy','zesty','snappy','perky','chipper','jolly','merry','upbeat'
]

const getDiceBearUrl = (style: string, seed: string) => 
 `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(seed)}`

const TIME_PERIODS = [
 { key:'early_morning', label:'Early Morning', icon: Sun, time:'5-7 AM' },
 { key:'morning', label:'Morning', icon: Coffee, time:'7-12 PM' },
 { key:'afternoon', label:'Afternoon', icon: Zap, time:'12-5 PM' },
 { key:'evening', label:'Evening', icon: Moon, time:'5-9 PM' },
 { key:'night', label:'Night', icon: Star, time:'9 PM+' },
]

export const Profile: React.FC = () => {
 const navigate = useNavigate()
 const [searchParams, setSearchParams] = useSearchParams()
 // setThemeStyle is gone: the gallery selects a theme by id, and the journal
 // themes are rows in that gallery like any other, so there is no separate
 // modern/diary mode to flip.
 const { setTheme: applyTheme, theme: currentTheme, isDiary, diaryFont, setDiaryFont, accentColor, setAccentColor, uiFont, setUIFont } = useTheme()
 
 // Store the original theme when entering preferences tab (for reset on leave).
 // Derived from Theme rather than re-listing it: this was a hand-copied union
 // and adding a theme silently broke the reset-on-leave path here.
 type ThemeValue = Theme | null
 const [originalTheme, setOriginalTheme] = useState<ThemeValue>(null)
 const originalThemeRef = useRef<ThemeValue>(null)
 
 // Keep ref updated for cleanup
 useEffect(() => {
 originalThemeRef.current = originalTheme
 }, [originalTheme])
 
 // Get active tab from URL, default to'profile'
 const activeTab = (searchParams.get('tab') as ProfileTab) ||'profile'
 const setActiveTab = (tab: ProfileTab) => {
 setSearchParams({ tab })
 }
 
 useProvideFeedbackContext({ pageType:'profile', extra: { tab: activeTab } })

 const [, setProfile] = useState<UserProfile | null>(null)
 const [isLoading, setIsLoading] = useState(true)
 const [saveMessage, setSaveMessage] = useState<{ type:'success' |'error', text: string } | null>(null)
 const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null)
 
 // Scheduling preferences state
 const [schedPrefs, setSchedPrefs] = useState<SchedulingPreferences>({
 wake_time:'07:00',
 sleep_time:'23:00',
 work_start_time:'09:00',
 work_end_time:'18:00',
 work_days: ['mon','tue','wed','thu','fri'],
 energy_levels: {},
 focus_duration_minutes: 45,
 break_duration_minutes: 10,
 buffer_between_tasks: 15,
 lunch_start_time:'12:00',
 lunch_duration_minutes: 60,
 primary_motivation:'',
 // How much in a day
 daily_focus_limit: 3,
 daily_hold_limit: 3,
 max_parallel_tasks: 3,
 default_horizon:'next_7_days',
 // Today Focus Preferences
 auto_backfill_focus: true,
 show_recurring_in_today: true,
 recurring_counts_toward_focus: false,
 enable_low_momentum_mode: true,
 low_momentum_focus_limit: 1,
 allow_carryover_incremental: true,
 max_incremental_in_focus: 1,
 })
 /**
  * Which scheduling keys this person has actually stored, from the API's
  * `answered` list. Everything in the GET falls back to a default, so without
  * this the page cannot tell "they chose 07:00" from "we assumed 07:00" — and
  * on 2026-09-06 every profile in production was the second case with nothing
  * on screen saying so.
  */
 const [schedAnswered, setSchedAnswered] = useState<string[]>([])
 const isAssumed = (k: keyof SchedulingPreferences) => !schedAnswered.includes(k)
 // Drafts. A popover edits its draft and only the Save writes it back, so a
 // stray click or an Escape never rewrites a setting (the InlinePopover
 // contract every item page in the app already follows).
 const [dCarryMode, setDCarryMode] = useState('banner')
 const [dTone, setDTone] = useState('supportive_best_friend')
 const [dFirst, setDFirst] = useState('')
 const [dLast, setDLast] = useState('')
 const [dDisplay, setDDisplay] = useState('')
 const [dTitle, setDTitle] = useState('')
 const [dBio, setDBio] = useState('')
 const [dTz, setDTz] = useState('UTC')
 const [dCity, setDCity] = useState('')
 const [dCountry, setDCountry] = useState('')
 const [dWake, setDWake] = useState('07:00')
 const [dSleep, setDSleep] = useState('23:00')
 const [dStart, setDStart] = useState('09:00')
 const [dEnd, setDEnd] = useState('17:00')
 const [dDays, setDDays] = useState<string[]>([])
 const [dLunch, setDLunch] = useState('12:00')
 const [dLunchLen, setDLunchLen] = useState(60)
 const [dFocus, setDFocus] = useState(45)
 const [dBreak, setDBreak] = useState(10)
 const [dBuffer, setDBuffer] = useState(15)
 const [dPerDay, setDPerDay] = useState(3)
 const [dHolds, setDHolds] = useState(3)
 const [dParallel, setDParallel] = useState(3)
 const [dHorizon, setDHorizon] = useState('next_7_days')
 const [dRecovery, setDRecovery] = useState(1)
 const [dCarry, setDCarry] = useState(1)
 const [dWhy, setDWhy] = useState('')
 const [schedLoading, setSchedLoading] = useState(false)
 const [, setSchedSaving] = useState(false)  // each popover shows its own busy state
 
 // Engine identity record — what the assistant has learned about the user from
 // conversations (read-only here; edited via My People). Null = none/engine down.
 const [hortonIdentity, setHortonIdentity] = useState<{
 id: string
 title: string
 description?: string
 metadata?: Record<string, unknown>
 } | null>(null)

 // Personal Info state
 const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
 first_name:'',
 last_name:'',
 display_name:'',
 title:'',
 bio:'',
 avatar_url:'',
 timezone:'UTC',
 country:'',
 city:'',
 })
 const [personalLoading, setPersonalLoading] = useState(false)
 const [, setPersonalSaving] = useState(false)  // each popover shows its own busy state
 const [showAvatarPicker, setShowAvatarPicker] = useState(false)
 const [selectedAvatarStyle, setSelectedAvatarStyle] = useState('avataaars')
 const [avatarMode, setAvatarMode] = useState<'static' |'animated'>('static')
 const [selectedAnimation, setSelectedAnimation] = useState('bounce')
 
 // App Preferences state
 const [appPrefs, setAppPrefs] = useState<AppPreferences>({
 theme_preference:'light',
 carryover_entry_mode:'banner',
 motivation_style:'supportive_best_friend',
 })
 const [prefsLoading, setPrefsLoading] = useState(false)
 const [, setPrefsSaving] = useState(false)  // each popover shows its own busy state
 
 // Dev Tools state
 const [showDevTools, setShowDevTools] = useState(false)
 const [itemCounts, setItemCounts] = useState<ItemCounts | null>(null)
 const [devToolsLoading, setDevToolsLoading] = useState(false)
 const [showClearConfirm, setShowClearConfirm] = useState(false)
 const [clearLoading, setClearLoading] = useState(false)
 const [clearResult, setClearResult] = useState<{
 total_deleted: number
 deleted_counts: Record<string, number | Record<string, unknown> | string>
 } | null>(null)

 // Security state
 const [securityData, setSecurityData] = useState<SecurityData | null>(null)
 const [securityLoading, setSecurityLoading] = useState(false)
 const [securityDays, setSecurityDays] = useState(30)
 const [resendingVerification, setResendingVerification] = useState(false)
 const [verificationMessage, setVerificationMessage] = useState<{ type:'success' |'error', text: string } | null>(null)

 useEffect(() => {
 loadProfileData()
 }, [])
 
 // Load data when tab changes
 useEffect(() => {
 if (activeTab ==='profile') {
 if (!personalLoading) loadPersonalInfo()
 if (!prefsLoading) loadAppPreferences()   // the theme lives here now
 } else if (activeTab ==='day') {
 if (!schedLoading) loadSchedulingPreferences()
 } else if (activeTab ==='attention') {
 if (!prefsLoading) loadAppPreferences()   // carryover + reminder policy
 } else if (activeTab ==='security' && !securityLoading) {
 loadSecurityData()
 }
 }, [activeTab])

 // Theme preview: capture original theme when entering preferences tab
 useEffect(() => {
 if (activeTab ==='profile') {
 // Save the current theme so we can restore it if user leaves without saving
 setOriginalTheme(currentTheme)
 } else if (originalTheme) {
 // Left the You tab - reset to original/saved theme
 applyTheme(originalTheme)
 setOriginalTheme(null)
 }
 }, [activeTab])

 // Reset theme on component unmount (navigating away from Profile entirely)
 useEffect(() => {
 return () => {
 if (originalThemeRef.current) {
 applyTheme(originalThemeRef.current)
 }
 }
 }, [applyTheme])

 const loadProfileData = async () => {
 try {
 setIsLoading(true)
 // The persona picker was removed on 2026-09-06: it wrote
 // horton_emotional_approach / horton_conversation_style, whose only
 // reader was src/original-main-horton (which nothing imports), and the
 // live chat client never puts a persona in any payload. Nothing here
 // fetches personas any more.
 const profileData = await profileService.getUserProfile()

 // Fetch MFA status
 try {
 const mfaRes = await fetch('/api/mfa/status', { credentials:'include' })
 if (mfaRes.ok) {
 const mfaData = await mfaRes.json()
 setMfaStatus(mfaData)
 }
 } catch (e) {
 console.error('Failed to fetch MFA status:', e)
 }

 setProfile(profileData)
 } catch (error) {
 console.error('Error loading profile:', error)
 setSaveMessage({ type:'error', text:'Failed to load profile data' })
 } finally {
 setIsLoading(false)
 }
 }
 
 const loadSchedulingPreferences = async () => {
 try {
 setSchedLoading(true)
 const res = await fetch('/api/scheduling-preferences', { credentials:'include' })
 const data = await res.json()
 if (data.success && data.preferences) {
 // Take only the keys we still render. The GET replies with the full
 // legacy set (mantras, activity_preferences, the meeting rules…), and
 // spreading it whole would put those straight back into the next PUT
 // even though no control edits them any more.
 const known = Object.keys(schedPrefsShape) as (keyof SchedulingPreferences)[]
 setSchedAnswered(Array.isArray(data.answered) ? data.answered : [])
 const picked: Partial<SchedulingPreferences> = {}
 for (const k of known) {
 if (data.preferences[k] !== undefined && data.preferences[k] !== null) {
 (picked as Record<string, unknown>)[k] = data.preferences[k]
 }
 }
 setSchedPrefs(prev => ({ ...prev, ...picked }))
 }
 } catch (error) {
 console.error('Error loading scheduling preferences:', error)
 } finally {
 setSchedLoading(false)
 }
 }
 
 /**
  * Save ONE fact (or several, when they are one decision).
  *
  * The patch is merged and sent in the same tick rather than going through
  * state first: `setSchedPrefs` is async, so saving after it would post the
  * value the person had BEFORE their edit. Throws on failure, which is what
  * InlinePopover wants — it shows the message and keeps the popover open with
  * the draft intact, instead of silently losing it.
  */
 const saveSchedulingPreferences = async (patch?: Partial<SchedulingPreferences>) => {
 const next = patch ? { ...schedPrefs, ...patch } : schedPrefs
 try {
 setSchedSaving(true)
 // Send ONLY what changed. The PUT copies whatever keys it is given, so
 // posting the whole object would stamp all twenty-odd of them onto the
 // record the first time you touched anything — and the server's
 // `answered` list would then report every row as a deliberate choice,
 // which is exactly the "a default rendered as an answer" problem this
 // page was rebuilt to end.
 const res = await fetch('/api/scheduling-preferences', {
 method:'PUT',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify(patch ?? next)
 })
 const data = await res.json().catch(() => ({ success: false }))
 if (!res.ok || !data.success) throw new Error(data.error ||'Could not save that')
 setSchedPrefs(next)
 setSchedAnswered(prev => [...new Set([...prev, ...Object.keys(patch || next)])])
 setSaveMessage({ type:'success', text:'Saved' })
 setTimeout(() => setSaveMessage(null), 2000)
 } finally {
 setSchedSaving(false)
 }
 }
 
 
 // ─────────────────────────────────────────────────────────────────────────
 // PERSONAL INFO FUNCTIONS
 // ─────────────────────────────────────────────────────────────────────────
 
 const loadPersonalInfo = async () => {
 try {
 setPersonalLoading(true)
 const res = await fetch('/api/profile/personal-info', { credentials:'include' })
 const data = await res.json()
 if (data.success && data.personal_info) {
 setPersonalInfo(prev => ({ ...prev, ...data.personal_info }))
 }
 } catch (error) {
 console.error('Error loading personal info:', error)
 } finally {
 setPersonalLoading(false)
 }
 // The engine-side identity record ("What the assistant knows about you") —
 // fire-and-forget; ensure=1 seeds a default identity from the profile so
 // every user has one (owner ask 2026-07-05). Never blocks the page.
 fetch('/api/horton-v3/identity?ensure=1', { credentials:'include' })
 .then(r => (r.ok ? r.json() : null))
 .then(d => setHortonIdentity(d?.identity ?? null))
 .catch(() => {})
 }
 
 /** Save one fact about you. See the note on saveSchedulingPreferences. */
 const savePersonalInfo = async (patch?: Partial<PersonalInfo>) => {
 const next = patch ? { ...personalInfo, ...patch } : personalInfo
 try {
 setPersonalSaving(true)
 // Only what changed — see the note on saveSchedulingPreferences.
 const res = await fetch('/api/profile/personal-info', {
 method:'PUT',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify(patch ?? next)
 })
 const data = await res.json().catch(() => ({ success: false }))
 if (!res.ok || !data.success) throw new Error(data.error ||'Could not save that')
 setPersonalInfo(next)
 setSaveMessage({ type:'success', text:'Saved' })
 setTimeout(() => setSaveMessage(null), 2000)
 } finally {
 setPersonalSaving(false)
 }
 }
 
 const updatePersonalField = (field: keyof PersonalInfo, value: string) => {
 setPersonalInfo(prev => ({ ...prev, [field]: value }))
 }
 
 // ─────────────────────────────────────────────────────────────────────────
 // APP PREFERENCES FUNCTIONS
 // ─────────────────────────────────────────────────────────────────────────
 
 const loadAppPreferences = async () => {
 try {
 setPrefsLoading(true)
 const res = await fetch('/api/profile/app-preferences', { credentials:'include' })
 const data = await res.json()
 if (data.success && data.preferences) {
 setAppPrefs(prev => ({ ...prev, ...data.preferences }))
 }
 } catch (error) {
 console.error('Error loading app preferences:', error)
 } finally {
 setPrefsLoading(false)
 }
 }
 
 /** Save one app preference. See the note on saveSchedulingPreferences. */
 const saveAppPreferences = async (patch?: Partial<AppPreferences>) => {
 const next = patch ? { ...appPrefs, ...patch } : appPrefs
 try {
 setPrefsSaving(true)
 // Only what changed — see the note on saveSchedulingPreferences.
 const res = await fetch('/api/profile/app-preferences', {
 method:'PUT',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify(patch ?? next)
 })
 const data = await res.json().catch(() => ({ success: false }))
 if (!res.ok || !data.success) throw new Error(data.error ||'Could not save that')
 setAppPrefs(next)
 // The theme is previewed live as you pick it, so the save is what makes
 // that preview permanent. Diary themes are owned by setThemeStyle and
 // must not be overwritten from here.
 if (!isDiary && patch && 'theme_preference' in patch) {
 const themeMap: Record<string,'light' |'dark' |'system'> = {
 'light':'light','dark':'dark','auto':'system',
 }
 const themeValue = themeMap[next.theme_preference] ||'system'
 applyTheme(themeValue)
 setOriginalTheme(themeValue)
 }
 setSaveMessage({ type:'success', text:'Saved' })
 setTimeout(() => setSaveMessage(null), 2000)
 } finally {
 setPrefsSaving(false)
 }
 }
 
 
 
 // ─────────────────────────────────────────────────────────────────────────
 // SECURITY FUNCTIONS
 // ─────────────────────────────────────────────────────────────────────────
 
 const loadSecurityData = async () => {
 try {
 setSecurityLoading(true)
 const result = await securityService.getDashboard(securityDays)
 setSecurityData(result)
 } catch (error) {
 console.error('Error loading security data:', error)
 } finally {
 setSecurityLoading(false)
 }
 }
 
 const handleTerminateSession = async (sessionId: number) => {
 try {
 await securityService.terminateSession(sessionId)
 loadSecurityData()
 } catch (err) {
 setSaveMessage({ type:'error', text: err instanceof Error ? err.message :'Failed to sign that device out' })
 }
 }
 
 const handleTerminateAllSessions = async () => {
 if (!confirm('Are you sure you want to terminate all other sessions?')) return
 try {
 await securityService.terminateAllSessions(true)
 loadSecurityData()
 setSaveMessage({ type:'success', text:'All other sessions terminated' })
 setTimeout(() => setSaveMessage(null), 3000)
 } catch (err) {
 setSaveMessage({ type:'error', text: err instanceof Error ? err.message :'Failed to sign the other devices out' })
 }
 }

 const handleResendVerification = async () => {
 try {
 setResendingVerification(true)
 setVerificationMessage(null)
 
 const response = await fetch('/api/send-verification-email', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include'
 })
 
 const data = await response.json()
 
 if (data.success) {
 setVerificationMessage({ type:'success', text:'Verification email sent! Please check your inbox.' })
 } else {
 setVerificationMessage({ type:'error', text: data.error ||'Failed to send verification email' })
 }
 
 // Clear message after 5 seconds
 setTimeout(() => setVerificationMessage(null), 5000)
 } catch (err) {
 console.error('Error resending verification:', err)
 setVerificationMessage({ type:'error', text:'Failed to send verification email' })
 setTimeout(() => setVerificationMessage(null), 5000)
 } finally {
 setResendingVerification(false)
 }
 }

 const loadItemCounts = async () => {
 try {
 setDevToolsLoading(true)
 const response = await fetch('/api/dev/count-items', { credentials:'include' })
 if (response.ok) {
 const data = await response.json()
 setItemCounts(data.counts)
 }
 } catch (error) {
 console.error('Error loading item counts:', error)
 } finally {
 setDevToolsLoading(false)
 }
 }

 const handleClearAllItems = async () => {
 try {
 setClearLoading(true)
 setClearResult(null)
 
 const response = await fetch('/api/dev/clear-all-items', {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' }
 })
 
 if (response.ok) {
 const result = await response.json()
 setClearResult(result)
 setShowClearConfirm(false)
 await loadItemCounts()
 }
 } catch (error) {
 console.error('Error clearing items:', error)
 } finally {
 setClearLoading(false)
 }
 }

 const toggleDevTools = () => {
 if (!showDevTools && !itemCounts) {
 loadItemCounts()
 }
 setShowDevTools(!showDevTools)
 }

 const totalItems = itemCounts ? Object.values(itemCounts).reduce((sum, count) => sum + count, 0) : 0
 if (isLoading) {
 return (
 <div className="flex items-center justify-center h-64">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
 </div>
 )
 }

 // ─────────────────────────────────────────────────────────────────────────
 // RENDER PERSONAL INFO TAB
 // ─────────────────────────────────────────────────────────────────────────
 
 const renderPersonalInfo = () => {
 if (personalLoading) {
 return (
 <div className="flex items-center justify-center h-64">
 <Loader2 className="h-8 w-8 animate-spin text-brand" />
 </div>
 )
 }

 return (
 <div className="space-y-6">
 {/* Avatar & Basic Info */}
 <SettingGroup
 title="About you"
 blurb="All three reach the coach: your name is how you are addressed, and the other two are context it keeps in mind when you talk."
 >
 {/* Avatar Section */}
 <div className="space-y-4">
 <div className="flex items-center gap-6">
 <div className="relative">
                <div className="w-24 h-24 rounded-full bg-ok flex items-center justify-center text-on-brand text-2xl font-semibold overflow-hidden">
 {personalInfo.avatar_url ? (
 <img src={personalInfo.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
 ) : (
 (personalInfo.first_name?.[0] || personalInfo.display_name?.[0] ||'?').toUpperCase()
 )}
 </div>
 <button 
 onClick={() => setShowAvatarPicker(!showAvatarPicker)}
 className="absolute bottom-0 right-0 p-1.5 bg-surface rounded-full shadow-lg border border-soft hover:bg-surface-alt"
 >
 <Camera className="h-4 w-4 text-secondary" />
 </button>
 </div>
 <div className="flex-1">
 <Button
 onClick={() => setShowAvatarPicker(!showAvatarPicker)}
 variant="outline"
 className="mb-2"
 >
 <Sparkles className="h-4 w-4 mr-2" />
 {showAvatarPicker ?'Hide Avatar Picker' :'Choose an Avatar'}
 </Button>
 <input
 type="text"
 value={personalInfo.avatar_url}
 onChange={(e) => updatePersonalField('avatar_url', e.target.value)}
 placeholder="Or paste a custom image URL"
 className="w-full px-4 py-2 border rounded-lg text-sm"
 />
 </div>
 </div>

 {/* Avatar Picker */}
 {showAvatarPicker && (
 <div className="bg-surface-alt rounded-xl p-4 border border-soft">
 {/* Mode Toggle: Static vs Animated */}
 <div className="flex items-center justify-between mb-4">
 <h4 className="font-medium text-primary flex items-center gap-2">
 <Palette className="h-4 w-4 text-brand" />
 Pick Your Avatar
 </h4>
 <div className="flex bg-soft rounded-lg p-1">
 <button
 onClick={() => setAvatarMode('static')}
 className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
 avatarMode ==='static'
 ?'bg-surface text-brand shadow-sm'
 :'text-secondary hover:text-primary'
 }`}
 >
 🖼️ Static
 </button>
 <button
 onClick={() => setAvatarMode('animated')}
 className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
 avatarMode ==='animated'
 ?'bg-surface text-brand shadow-sm'
 :'text-secondary hover:text-primary'
 }`}
 >
 ✨ Animated
 </button>
 </div>
 </div>

 {/* Static Avatars (DiceBear) */}
 {avatarMode ==='static' && (
 <>
 {/* Style Selector */}
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-soft">
 {DICEBEAR_STYLES.map(style => (
 <button
 key={style.id}
 onClick={() => setSelectedAvatarStyle(style.id)}
 className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
 selectedAvatarStyle === style.id
 ?'bg-brand text-on-brand shadow-lg'
 :'bg-surface text-secondary hover:bg-brand-subtle border border-soft'
 }`}
 >
 {style.name}
 </button>
 ))}
 </div>

 {/* Avatar Grid */}
 <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-3 max-h-64 overflow-y-auto p-1">
 {AVATAR_SEEDS.map(seed => {
 const avatarUrl = getDiceBearUrl(selectedAvatarStyle, seed)
 const isSelected = personalInfo.avatar_url === avatarUrl
 return (
 <button
 key={seed}
 onClick={() => updatePersonalField('avatar_url', avatarUrl)}
 className={`relative group aspect-square rounded-xl overflow-hidden transition-all hover:scale-110 hover:shadow-lg ${
 isSelected
 ?'ring-4 ring-brand ring-offset-2 scale-105 shadow-lg'
 :'hover:ring-2 hover:ring-brand/50'
 }`}
 title={`Select ${seed} avatar`}
 >
 <img
 src={avatarUrl}
 alt={`Avatar ${seed}`}
 className="w-full h-full object-cover bg-surface"
 loading="lazy"
 />
 {isSelected && (
 <div className="absolute inset-0 bg-brand/20 flex items-center justify-center">
 <CheckCircle className="h-6 w-6 text-brand" />
 </div>
 )}
 </button>
 )
 })}
 </div>

 {/* Footer */}
 <div className="mt-4 pt-3 border-t border-soft flex items-center justify-between">
 <p className="text-xs text-muted">
 Powered by <a href="https://dicebear.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">DiceBear</a> - Free for commercial use
 </p>
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 const randomSeed = `user_${Date.now()}_${Math.random().toString(36).substring(7)}`
 updatePersonalField('avatar_url', getDiceBearUrl(selectedAvatarStyle, randomSeed))
 }}
 >
 <RefreshCw className="h-3 w-3 mr-1" />
 Random
 </Button>
 </div>
 </>
 )}

 {/* Animated Avatars (DiceBear with CSS animations) */}
 {avatarMode ==='animated' && (
 <>
 {/* Avatar Style Selector */}
 <div className="mb-3">
 <p className="text-app-micro text-muted mb-2">Choose avatar style:</p>
 <div className="flex gap-2 flex-wrap">
 {ANIMATED_DICEBEAR_STYLES.map(style => (
 <button
 key={style.id}
 onClick={() => setSelectedAvatarStyle(style.id)}
 className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
 selectedAvatarStyle === style.id
 ?'bg-brand text-on-brand shadow-lg'
 :'bg-surface text-secondary hover:bg-brand-subtle border border-soft'
 }`}
 >
 {style.name}
 </button>
 ))}
 </div>
 </div>

 {/* Animation Effect Selector */}
 <div className="mb-4">
 <p className="text-app-micro text-muted mb-2">Choose animation effect:</p>
 <div className="flex gap-2 flex-wrap">
 {ANIMATED_AVATAR_STYLES.map(style => (
 <button
 key={style.id}
 onClick={() => setSelectedAnimation(style.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedAnimation === style.id
                        ?'bg-brand text-on-brand shadow-lg'
                        :'bg-surface text-secondary hover:bg-brand-subtle border border-soft'
 }`}
 >
 {style.name}
 </button>
 ))}
 </div>
 </div>

 {/* Animated Avatar Grid */}
 <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 max-h-72 overflow-y-auto p-1">
 {ANIMATED_AVATAR_SEEDS.map(seed => {
 const avatarUrl = getDiceBearUrl(selectedAvatarStyle, seed)
 const isSelected = personalInfo.avatar_url === avatarUrl
 return (
 <button
 key={seed}
 onClick={() => updatePersonalField('avatar_url', avatarUrl)}
                    className={`relative group aspect-square rounded-xl overflow-hidden transition-all hover:shadow-lg bg-brand-subtle ${
 isSelected
 ?'ring-4 ring-brand ring-offset-2 shadow-lg'
 :'hover:ring-2 hover:ring-brand/50'
 }`}
 title={`Select ${seed} avatar`}
 >
 <img
 src={avatarUrl}
 alt={`Avatar ${seed}`}
 className="w-full h-full object-cover"
 loading="lazy"
 style={{
 animation: selectedAnimation ==='bounce' ?'avatar-bounce 1s ease-in-out infinite' :
 selectedAnimation ==='pulse' ?'avatar-pulse 2s ease-in-out infinite' :
 selectedAnimation ==='spin' ?'avatar-spin 3s linear infinite' :
 selectedAnimation ==='wiggle' ?'avatar-wiggle 0.5s ease-in-out infinite' :
 selectedAnimation ==='float' ?'avatar-float 3s ease-in-out infinite' :
 selectedAnimation ==='jello' ?'avatar-jello 1s ease-in-out infinite' :
 undefined
 }}
 />
 {isSelected && (
 <div className="absolute inset-0 bg-brand/20 flex items-center justify-center">
 <CheckCircle className="h-6 w-6 text-brand" />
 </div>
 )}
 </button>
 )
 })}
 </div>

 {/* Footer */}
 <div className="mt-4 pt-3 border-t border-soft flex items-center justify-between">
 <p className="text-xs text-muted">
 Powered by <a href="https://dicebear.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">DiceBear</a> - Free for commercial use
 </p>
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 const randomSeed = `anim_${Date.now()}_${Math.random().toString(36).substring(7)}`
 updatePersonalField('avatar_url', getDiceBearUrl(selectedAvatarStyle, randomSeed))
 }}
 >
 <RefreshCw className="h-3 w-3 mr-1" />
 Random
 </Button>
 </div>

 {/* CSS for custom animations */}
 <style>{`
 @keyframes avatar-bounce {
 0%, 100% { transform: translateY(0); }
 50% { transform: translateY(-8px); }
 }
 @keyframes avatar-pulse {
 0%, 100% { transform: scale(1); opacity: 1; }
 50% { transform: scale(1.05); opacity: 0.8; }
 }
 @keyframes avatar-spin {
 from { transform: rotate(0deg); }
 to { transform: rotate(360deg); }
 }
 @keyframes avatar-wiggle {
 0%, 100% { transform: rotate(-5deg); }
 50% { transform: rotate(5deg); }
 }
 @keyframes avatar-float {
 0%, 100% { transform: translateY(0) rotate(0deg); }
 25% { transform: translateY(-5px) rotate(2deg); }
 75% { transform: translateY(-5px) rotate(-2deg); }
 }
 @keyframes avatar-jello {
 0%, 100% { transform: scale3d(1, 1, 1); }
 30% { transform: scale3d(1.25, 0.75, 1); }
 40% { transform: scale3d(0.75, 1.25, 1); }
 50% { transform: scale3d(1.15, 0.85, 1); }
 65% { transform: scale3d(0.95, 1.05, 1); }
 75% { transform: scale3d(1.05, 0.95, 1); }
 }
 `}</style>
 </>
 )}
 </div>
 )}
 </div>


 {/* Your facts, each editable where it is shown and saving on its own —
     the "Save Personal Info" button that used to sit at the bottom of this
     tab covered five unrelated fields and stood nowhere near any of them. */}
 <div className="pt-2">
 <SettingRow
 label="What to call you"
 because="How you are addressed everywhere, and the name the coach learns."
 value={personalInfo.display_name?.trim()
 || [personalInfo.first_name, personalInfo.last_name].filter(Boolean).join(' ').trim()}
 width="w-96"
 onOpen={() => {
 setDFirst(personalInfo.first_name ||'')
 setDLast(personalInfo.last_name ||'')
 setDDisplay(personalInfo.display_name ||'')
 }}
 onSave={() => savePersonalInfo({ first_name: dFirst, last_name: dLast, display_name: dDisplay })}
 testId="set-name"
 >
 <div className="space-y-3">
 <div className="grid grid-cols-2 gap-3">
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">First</span>
 <input value={dFirst} onChange={e => setDFirst(e.target.value)} className={fieldCls} /></label>
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Last</span>
 <input value={dLast} onChange={e => setDLast(e.target.value)} className={fieldCls} /></label>
 </div>
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Called</span>
 <input value={dDisplay} onChange={e => setDDisplay(e.target.value)}
 placeholder="What you'd rather be called" className={fieldCls} /></label>
 </div>
 </SettingRow>

 <SettingRow
 label="What you do"
 because="Your work, or whatever takes up most of your days. The coach knows it when it suggests things."
 value={personalInfo.title?.trim()}
 width="w-96"
 onOpen={() => setDTitle(personalInfo.title ||'')}
 onSave={() => savePersonalInfo({ title: dTitle })}
 testId="set-title"
 >
 <input value={dTitle} onChange={e => setDTitle(e.target.value)}
 placeholder="e.g. Nurse, or looking after two kids" className={fieldCls} />
 </SettingRow>

 <SettingRow
 label="About you"
 because="Anything worth remembering. It is carried into every conversation, in your own words."
 value={personalInfo.bio?.trim()}
 width="w-96"
 onOpen={() => setDBio(personalInfo.bio ||'')}
 onSave={() => savePersonalInfo({ bio: dBio })}
 testId="set-bio"
 >
 <textarea rows={4} value={dBio} onChange={e => setDBio(e.target.value)}
 placeholder="Anything worth knowing about you" className={`${fieldCls} resize-none`} />
 </SettingRow>
 </div>
 </SettingGroup>

 {/* What the assistant knows about you — the engine identity record, kept
     updated by conversations (owner ask 2026-07-05). Read-only here;
     edit the details on My People. */}
 <SettingGroup
 title="What it has learned about you"
 blurb={<>It grows as you talk. <a href="/my-people" className="underline hover:text-primary">Edit on My People</a>.</>}
 >
 {hortonIdentity ? (
 <div className="space-y-2 text-sm">
 <div><span className="text-muted">Name:</span>{' '}<span className="font-medium text-primary">{hortonIdentity.title}</span></div>
 {Array.isArray(hortonIdentity.metadata?.aliases) && (hortonIdentity.metadata!.aliases as string[]).length > 0 && (
 <div><span className="text-muted">Also goes by:</span>{' '}{(hortonIdentity.metadata!.aliases as string[]).join(', ')}</div>
 )}
 {hortonIdentity.description && (
 <div><span className="text-muted">About:</span>{' '}{hortonIdentity.description}</div>
 )}
 {Array.isArray(hortonIdentity.metadata?.facts) && (hortonIdentity.metadata!.facts as string[]).length > 0 && (
 <div>
 <span className="text-muted">Things you've shared:</span>
 <ul className="mt-1 list-disc pl-5 space-y-0.5">
 {(hortonIdentity.metadata!.facts as string[]).slice(0, 8).map((f, i) => (
 <li key={i}>{f}</li>
 ))}
 </ul>
 </div>
 )}
 </div>
 ) : (
 <p className="text-sm text-muted">
 Nothing yet — as you chat, {BRAND.assistant} builds a picture of who you are.
 </p>
 )}
 </SettingGroup>


 <SettingGroup
 title="Where you are"
 blurb="Your timezone is the one thing here that changes how the app behaves: it decides what every date and every reminder time means."
 >
 <SettingRow
 label="Timezone"
 value={(personalInfo.timezone ||'UTC').replace(/_/g,' ')}
 onOpen={() => setDTz(personalInfo.timezone ||'UTC')}
 onSave={() => savePersonalInfo({ timezone: dTz })}
 testId="set-timezone"
 >
 <select value={dTz} onChange={e => setDTz(e.target.value)} className={fieldCls}>
 {[...new Set([dTz, ...TIMEZONES])].map(tz => (
 <option key={tz} value={tz}>{tz.replace(/_/g,' ')}</option>
 ))}
 </select>
 </SettingRow>

 <SettingRow
 label="Town and country"
 because="Roughly where you are, for conversations where somewhere nearby matters."
 value={[personalInfo.city, personalInfo.country].filter(Boolean).join(', ')}
 width="w-96"
 onOpen={() => { setDCity(personalInfo.city ||''); setDCountry(personalInfo.country ||'') }}
 onSave={() => savePersonalInfo({ city: dCity, country: dCountry })}
 testId="set-place"
 >
 <div className="grid grid-cols-2 gap-3">
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Town</span>
 <input value={dCity} onChange={e => setDCity(e.target.value)} className={fieldCls} /></label>
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Country</span>
 <input value={dCountry} onChange={e => setDCountry(e.target.value)} className={fieldCls} /></label>
 </div>
 </SettingRow>
 </SettingGroup>
 </div>
 )
 }

 // ─────────────────────────────────────────────────────────────────────────
 // RENDER APP PREFERENCES TAB
 // ─────────────────────────────────────────────────────────────────────────
 
 /** The look of the app: theme, accent, fonts. */
 const renderLook = () => {
 if (prefsLoading) {
 return (
 <div className="flex items-center justify-center h-64">
 <Loader2 className="h-8 w-8 animate-spin text-brand" />
 </div>
 )
 }

 return (
 <div className="space-y-6">
 {/* Appearance */}
 <SettingGroup title="How it looks" blurb="Theme, accent and typeface. Changes apply as you pick them.">
 {/* Theme gallery. Each tile previews itself: the .theme-light/.theme-<id>
 pair puts that theme's real tokens in scope, so the swatch is painted by
 the same CSS the page would use rather than by hardcoded hex that could
 drift. theme-light underneath supplies the baseline for tokens a theme
 does not override — see scripts/check-theme-scopes.mjs. */}
 <div>
 <label className="text-app-body text-primary mb-1 block">Theme</label>
 <p className="text-app-micro text-muted mb-3">
 Light and dark are the two the app follows elsewhere; the rest are variations on them.
 </p>
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
 {THEMES.map(t => {
 const selected = currentTheme === t.id
 return (
 <button
 key={t.id}
 onClick={() => {
 applyTheme(t.id)
 // A theme pick is its own decision — the page already shows the
 // result, so an extra "Save Preferences" button at the bottom was
 // only ever a second click for something you had already seen.
 // The server preference only understands light and dark.
 if (t.id ==='light' || t.id ==='dark') {
 void saveAppPreferences({ theme_preference: t.id }).catch(() => {
 setSaveMessage({ type:'error', text:'Could not save that theme' })
 })
 }
 }}
 aria-pressed={selected}
 className={`rounded-xl border-2 overflow-hidden transition-all text-left ${
 selected ?'border-brand' :'border-soft hover:border-strong'
 }`}
 >
 <div className={`theme-light theme-${t.id} p-3 bg-default`}>
 <div className="rounded-lg border border-soft bg-surface p-2 flex items-center gap-2">
 <span className="h-5 w-5 rounded-full shrink-0" style={{ background:'var(--brand-primary)' }} />
 <span className="flex-1 min-w-0">
 <span className="block h-1.5 w-full rounded-full" style={{ background:'var(--text-primary)', opacity: .8 }} />
 <span className="block h-1.5 w-2/3 rounded-full mt-1" style={{ background:'var(--text-tertiary)' }} />
 </span>
 </div>
 </div>
 <div className="px-3 py-2 bg-surface">
 <div className="font-medium text-sm flex items-center gap-1.5">
 {t.label}
 {selected && <Check className="h-3.5 w-3.5 text-brand" />}
 </div>
 <div className="text-app-micro text-muted">{t.hint}</div>
 </div>
 </button>
 )
 })}
 </div>
 </div>

 {/* Dear Diary Font Selection - Only shown when diary theme is active */}
 {isDiary && (
 <div className="pt-4 border-t border-soft">
 <label className="text-sm font-medium mb-3 block flex items-center gap-2">
 <span>✍️</span> Handwriting Style
 </label>
 <p className="text-xs text-muted mb-4">
 Choose a handwritten font for your Dear Diary theme. 
 <span className="text-ok ml-1">✓ Recommended</span> fonts work best across all platforms (TV, mobile, tablet).
 </p>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 {DIARY_FONTS.map(font => (
 <button
 key={font.id}
 onClick={() => setDiaryFont(font.id)}
 className={`p-4 rounded-xl border-2 transition-all text-left relative ${
 diaryFont === font.id
 ?'border-warn bg-warn/10'
 :'border-soft hover:border-soft'
 }`}
 >
 {font.recommended && (
 <span className="absolute -top-2 -right-2 bg-ok text-on-brand text-xs px-2 py-0.5 rounded-full font-medium">
 ✓
 </span>
 )}
 <div 
 className="text-2xl mb-2"
 style={{ fontFamily: font.fontFamily }}
 >
 {BRAND.name}
 </div>
 <div className="text-sm font-medium text-secondary">{font.name}</div>
 <div className="text-xs text-muted mt-1">{font.description}</div>
 <div className="text-xs text-muted mt-2 leading-tight">{font.platformNotes}</div>
 </button>
 ))}
 </div>
 
 {/* Advanced Theme Builder Link */}
 <div className="mt-6 p-4 rounded-xl bg-warn/10 border border-warn/30">
 <div className="flex items-center justify-between">
 <div>
 <h4 className="font-medium text-warn flex items-center gap-2">
 ✨ Want more customization?
 </h4>
 <p className="text-sm text-warn mt-1">
 Use the Theme Builder to customize colors, paper textures, and more!
 </p>
 </div>
 <div className="flex flex-wrap gap-3">
 <button
 onClick={() => navigate('/theme-builder')}
 className="px-4 py-2 bg-warn hover:opacity-90 text-on-brand rounded-lg font-medium text-sm transition-colors"
 >
 🎨 Theme Builder
 </button>
 <button
 onClick={() => navigate('/diary-showcase')}
 className="px-4 py-2 bg-brand hover:bg-brand-hover text-on-brand rounded-lg font-medium text-sm transition-colors"
 >
 ✨ Diary Showcase
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Accent Color Picker */}
 <div className="pt-4 border-t border-soft">
 <label className="text-app-body text-primary mb-1 block">Accent colour</label>
 <p className="text-app-micro text-muted mb-3">
 Page headers, pagination and the active state of every button.
 </p>
 <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
 {ACCENT_COLORS.map(color => (
 <button
 key={color.id}
 onClick={() => setAccentColor(color.id)}
 className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
 accentColor === color.id
 ?'border-primary shadow-md'
 :'border-soft hover:border-soft'
 }`}
 >
 <div
 className="accent-swatch w-8 h-8 rounded-full ring-2 ring-offset-2 ring-offset-surface transition-all"
 data-accent={color.id}
 />
 <span className="text-xs font-medium text-secondary">{color.name}</span>
 </button>
 ))}
 </div>
 </div>

 {/* UI Font Picker */}
 <div className="pt-4 border-t border-soft">
 <label className="text-app-body text-primary mb-1 block">Typeface</label>
 <p className="text-app-micro text-muted mb-3">
 Used everywhere. It changes as you pick, so you can read a line before you keep it.
 </p>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 {UI_FONTS.map(font => (
 <button
 key={font.id}
 onClick={() => setUIFont(font.id)}
 className={`p-4 rounded-xl border-2 transition-all text-left ${
 uiFont === font.id
 ?'border-primary bg-surface-alt shadow-md'
 :'border-soft hover:border-soft'
 }`}
 >
 <div className="text-xl mb-2" style={{ fontFamily: font.fontFamily }}>
 {BRAND.name}
 </div>
 <div className="text-sm font-medium text-secondary">{font.name}</div>
 <div className="text-xs text-muted mt-1 truncate" style={{ fontFamily: font.fontFamily }}>
 The quick brown fox
 </div>
 </button>
 ))}
 </div>
 </div>
 </SettingGroup>
 </div>
 )
 }

 /** How the app reaches you: reminder insistence, quiet hours, carryover. */
 const renderAttention = () => {
 if (prefsLoading) {
 return (
 <div className="flex items-center justify-center h-64">
 <Loader2 className="h-8 w-8 animate-spin text-brand" />
 </div>
 )
 }

 return (
 <div className="space-y-4">
 {/* One home for every way the app speaks first. ReminderAttentionSettings
     owns its own two policies and saves them itself; the rest are rows. */}
 <SettingGroup
 title={`How ${BRAND.assistant} reaches you`}
 blurb="When it speaks up, how insistently, and what it honours while you rest."
 >
 <div className="pb-2"><ReminderAttentionSettings /></div>

 <SettingRow
 label="Yesterday's unfinished work"
 because="How something still waiting gets raised the next morning."
 value={carryoverWord(appPrefs.carryover_entry_mode)}
 onOpen={() => setDCarryMode(appPrefs.carryover_entry_mode ||'banner')}
 onSave={() => saveAppPreferences({ carryover_entry_mode: dCarryMode })}
 width="w-96"
 testId="set-carryover-mode"
 >
 <div className="space-y-2">
 {[
 { value:'banner', label:'Show a banner', description:'A quiet banner on Today. Open it when you are ready.' },
 { value:'modal', label:'Ask me when I open Today', description:'Decide about each one straight away.' },
 { value:'disabled', label:'Leave it alone', description:'Nothing is raised. It stays in your backlog.' },
 ].map(o => (
 <label key={o.value}
 className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 ${
 dCarryMode === o.value ?'border-brand-subtle bg-brand-subtle' :'border-soft'}`}>
 <input type="radio" name="carryover_draft" value={o.value}
 checked={dCarryMode === o.value}
 onChange={e => setDCarryMode(e.target.value)}
 className="mt-1 accent-brand" />
 <span>
 <span className="block text-app-body font-medium text-primary">{o.label}</span>
 <span className="mt-0.5 block text-app-micro text-muted">{o.description}</span>
 </span>
 </label>
 ))}
 </div>
 </SettingRow>

 {/* Wired 2026-09-07. motivator-service has always read
     profile['motivation_style'] to pick the wording of every encouragement,
     and NOTHING wrote it — so every person got the default tone forever.
     This is the honest version of the persona picker that was removed:
     three tones the motivator genuinely knows how to speak in. */}
 <SettingRow
 label="It speaks like"
 because="The tone of every nudge and every bit of encouragement."
 value={toneWord(appPrefs.motivation_style)}
 assumed={!appPrefs.motivation_style}
 width="w-96"
 onOpen={() => setDTone(appPrefs.motivation_style ||'supportive_best_friend')}
 onSave={() => saveAppPreferences({ motivation_style: dTone })}
 testId="set-tone"
 >
 <div className="space-y-2">
 {MOTIVATION_STYLES.map(s => (
 <label key={s.value}
 className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 ${
 dTone === s.value ?'border-brand-subtle bg-brand-subtle' :'border-soft'}`}>
 <input type="radio" name="tone_draft" value={s.value}
 checked={dTone === s.value}
 onChange={e => setDTone(e.target.value)}
 className="mt-1 accent-brand" />
 <span>
 <span className="block text-app-body font-medium text-primary capitalize">{s.label}</span>
 <span className="mt-0.5 block text-app-micro text-muted">{s.blurb}</span>
 </span>
 </label>
 ))}
 </div>
 </SettingRow>
 </SettingGroup>
 </div>
 )
 }

 // Render scheduling preferences section
 /**
  * Your day: the shape of it, and how much of it to fill.
  *
  * Rows, not a form. Each row is a sentence — what it is, what it is set to,
  * and what it changes — and each value carries its own Save (SettingRow, on
  * the same InlinePopover every item page uses). There used to be two
  * page-level "Save Preferences" buttons on this tab alone, neither near the
  * thing you had just changed.
  *
  * A value nobody has set reads as "assumed", because the API hands back a
  * full set of defaults for every unset field and the page cannot otherwise
  * tell a choice from a guess.
  */
 const renderSchedulingPreferences = () => {
 if (schedLoading) {
 return (
 <div className="flex items-center justify-center h-64">
 <Loader2 className="h-8 w-8 animate-spin text-brand" />
 </div>
 )
 }

 const P = schedPrefs
 const save = (patch: Partial<SchedulingPreferences>) => () => saveSchedulingPreferences(patch)

 return (
 <div className="space-y-4">
 <SettingGroup
 title="The shape of your day"
 blurb="Nothing is ever suggested outside these hours."
 >
 <SettingRow
 label="You are awake"
 because="Morning habits anchor to when you wake; evening ones to when you sleep."
 value={`${clockWord(P.wake_time)} to ${clockWord(P.sleep_time)}`}
 assumed={isAssumed('wake_time') && isAssumed('sleep_time')}
 onOpen={() => { setDWake(P.wake_time); setDSleep(P.sleep_time) }}
 onSave={() => saveSchedulingPreferences({ wake_time: dWake, sleep_time: dSleep })}
 testId="set-awake"
 >
 <div className="grid grid-cols-2 gap-3">
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Wake</span>
 <input type="time" value={dWake} onChange={e => setDWake(e.target.value)} className={fieldCls} /></label>
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Sleep</span>
 <input type="time" value={dSleep} onChange={e => setDSleep(e.target.value)} className={fieldCls} /></label>
 </div>
 </SettingRow>

 <SettingRow
 label="You work"
 because="Work-hours slots only exist inside this, and only on the days you pick."
 value={`${clockWord(P.work_start_time)}–${clockWord(P.work_end_time)}, ${daysWord(P.work_days)}`}
 assumed={isAssumed('work_start_time') && isAssumed('work_days')}
 width="w-96"
 onOpen={() => { setDStart(P.work_start_time); setDEnd(P.work_end_time); setDDays(P.work_days) }}
 onSave={() => saveSchedulingPreferences({ work_start_time: dStart, work_end_time: dEnd, work_days: dDays })}
 testId="set-work"
 >
 <div className="space-y-3">
 <div className="grid grid-cols-2 gap-3">
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Starts</span>
 <input type="time" value={dStart} onChange={e => setDStart(e.target.value)} className={fieldCls} /></label>
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">Ends</span>
 <input type="time" value={dEnd} onChange={e => setDEnd(e.target.value)} className={fieldCls} /></label>
 </div>
 <div>
 <span className="mb-1.5 block text-app-micro text-secondary">Days you work</span>
 <div className="flex flex-wrap gap-1.5">
 {DAY_KEYS.map(d => (
 <button key={d} type="button"
 onClick={() => setDDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
 aria-pressed={dDays.includes(d)}
 className={`rounded-lg border px-2.5 py-1 text-app-micro ${dDays.includes(d)
 ?'border-brand bg-brand-subtle font-medium text-brand'
 :'border-soft text-secondary hover:bg-surface-alt'}`}>
 {d[0].toUpperCase() + d.slice(1)}
 </button>
 ))}
 </div>
 <p className="mt-2 text-app-micro text-muted">
 A day you do not work has no working hours at all — the whole day is yours.
 </p>
 </div>
 </div>
 </SettingRow>

 <SettingRow
 label="Lunch"
 because="Also the afternoon anchor — anything suggested for the afternoon starts once lunch ends."
 value={`${clockWord(P.lunch_start_time)} for ${minutesWord(P.lunch_duration_minutes)}`}
 assumed={isAssumed('lunch_start_time')}
 onOpen={() => { setDLunch(P.lunch_start_time); setDLunchLen(P.lunch_duration_minutes) }}
 onSave={() => saveSchedulingPreferences({ lunch_start_time: dLunch, lunch_duration_minutes: dLunchLen })}
 testId="set-lunch"
 >
 <div className="grid grid-cols-2 gap-3">
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">From</span>
 <input type="time" value={dLunch} onChange={e => setDLunch(e.target.value)} className={fieldCls} /></label>
 <label className="block"><span className="mb-1 block text-app-micro text-secondary">For</span>
 <select value={dLunchLen} onChange={e => setDLunchLen(Number(e.target.value))} className={fieldCls}>
 {[30, 45, 60, 90].map(n => <option key={n} value={n}>{minutesWord(n)}</option>)}
 </select></label>
 </div>
 </SettingRow>

 <SettingRow
 label="You concentrate in blocks of"
 because="A working gap shorter than this is never offered as focus time."
 value={minutesWord(P.focus_duration_minutes)}
 assumed={isAssumed('focus_duration_minutes')}
 onOpen={() => setDFocus(P.focus_duration_minutes)}
 onSave={save({ focus_duration_minutes: dFocus })}
 testId="set-focus"
 >
 <select value={dFocus} onChange={e => setDFocus(Number(e.target.value))} className={fieldCls}>
 {[25, 45, 60, 90].map(n => <option key={n} value={n}>{minutesWord(n)}</option>)}
 </select>
 </SettingRow>

 <SettingRow
 label="Between blocks"
 because="Left free between two focus blocks, so a long stretch is not one unbroken slab."
 value={minutesWord(P.break_duration_minutes)}
 assumed={isAssumed('break_duration_minutes')}
 onOpen={() => setDBreak(P.break_duration_minutes)}
 onSave={save({ break_duration_minutes: dBreak })}
 testId="set-break"
 >
 <select value={dBreak} onChange={e => setDBreak(Number(e.target.value))} className={fieldCls}>
 {[5, 10, 15, 20].map(n => <option key={n} value={n}>{minutesWord(n)}</option>)}
 </select>
 </SettingRow>

 <SettingRow
 label="Breathing room around anything booked"
 because="Nothing is placed flush against a meeting on either side."
 value={minutesWord(P.buffer_between_tasks)}
 assumed={isAssumed('buffer_between_tasks')}
 onOpen={() => setDBuffer(P.buffer_between_tasks)}
 onSave={save({ buffer_between_tasks: dBuffer })}
 testId="set-buffer"
 >
 <select value={dBuffer} onChange={e => setDBuffer(Number(e.target.value))} className={fieldCls}>
 {[0, 5, 10, 15, 30].map(n => <option key={n} value={n}>{n === 0 ?'none' : minutesWord(n)}</option>)}
 </select>
 </SettingRow>
 </SettingGroup>

 <SettingGroup
 title="How much in a day"
 blurb="Ceilings, not targets — nothing here asks you to fill them. The first three cap different things: how much is put in front of you, how much of your calendar gets reserved, and how much you may have half-finished at once."
 >
 <SettingRow
 label="Things a day"
 because="The most that will ever appear on your Today list. Reach it and nothing more is added until tomorrow."
 value={countWord(P.daily_focus_limit,'thing')}
 assumed={isAssumed('daily_focus_limit')}
 onOpen={() => setDPerDay(P.daily_focus_limit)}
 onSave={save({ daily_focus_limit: dPerDay })}
 testId="set-per-day"
 >
 <select value={dPerDay} onChange={e => setDPerDay(Number(e.target.value))} className={fieldCls}>
 {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{countWord(n,'thing')}</option>)}
 </select>
 </SettingRow>

 <SettingRow
 label="Time blocked out a day"
 because="Different from the row above: something can be asked of you without a slot being reserved for it. This caps the slots."
 value={countWord(P.daily_hold_limit,'block')}
 assumed={isAssumed('daily_hold_limit')}
 onOpen={() => setDHolds(P.daily_hold_limit)}
 onSave={save({ daily_hold_limit: dHolds })}
 testId="set-holds"
 >
 <select value={dHolds} onChange={e => setDHolds(Number(e.target.value))} className={fieldCls}>
 {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{countWord(n,'block')}</option>)}
 </select>
 </SettingRow>

 <SettingRow
 label="Started but not finished"
 because="Counted across every day, not per day. Once this many are open, nothing new is offered until you finish one — but things already underway still show."
 value={atATimeWord(P.max_parallel_tasks)}
 assumed={isAssumed('max_parallel_tasks')}
 onOpen={() => setDParallel(P.max_parallel_tasks)}
 onSave={save({ max_parallel_tasks: dParallel })}
 testId="set-parallel"
 >
 <select value={dParallel} onChange={e => setDParallel(Number(e.target.value))} className={fieldCls}>
 {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{countWord(n,'thing')}</option>)}
 </select>
 </SettingRow>

 <SettingRow
 label="Plan ahead"
 because="When you ask for something to be scheduled, how far into the future it may be placed."
 value={horizonWord(P.default_horizon)}
 assumed={isAssumed('default_horizon')}
 onOpen={() => setDHorizon(P.default_horizon)}
 onSave={save({ default_horizon: dHorizon })}
 testId="set-horizon"
 >
 <select value={dHorizon} onChange={e => setDHorizon(e.target.value)} className={fieldCls}>
 {['today','tomorrow','next_3_days','next_7_days','next_14_days','next_30_days','next_90_days','next_365_days']
 .map(k => <option key={k} value={k}>{horizonWord(k)}</option>)}
 </select>
 <p className="mt-2 text-app-micro text-muted">
 Anything that repeats without an end date is laid out at least 90 days out regardless.
 </p>
 </SettingRow>

 {/* Wired 2026-09-07. The orchestrator has always READ this and nothing
     ever wrote it, so every person was capped at 1 on a hard day. */}
 <SettingRow
 label="On harder days, ease off to"
 because={P.enable_low_momentum_mode
 ?'After a run of quiet days it lowers the ceiling for a while rather than letting things pile up. Whichever number is smaller wins, so this never hands you a bigger day.'
 :'Only applies while "Ease off on harder days" is on — see What Today shows, below.'}
 value={recoveryWord(P.enable_low_momentum_mode, P.low_momentum_focus_limit, P.daily_focus_limit)}
 assumed={isAssumed('low_momentum_focus_limit')}
 onOpen={() => setDRecovery(P.low_momentum_focus_limit)}
 onSave={save({ low_momentum_focus_limit: dRecovery })}
 testId="set-recovery-limit"
 >
 <select value={dRecovery} onChange={e => setDRecovery(Number(e.target.value))} className={fieldCls}>
 {[1,2,3,4,5].map(n => <option key={n} value={n}>{countWord(n,'thing')}</option>)}
 </select>
 <p className="mt-2 text-app-micro text-muted">
 Whichever is smaller wins, so this never gives you a bigger day than your usual limit.
 </p>
 </SettingRow>

 {/* Wired 2026-09-07 — same story: read by the focus selector, written by
     nothing, so everyone carried exactly one thing over. */}
 <SettingRow
 label="Carry over what you already started"
 because="Unfinished work gets first claim on today’s places, before anything new is added. Set it to none and each day starts clean."
 value={carryInWord(P.allow_carryover_incremental, P.max_incremental_in_focus)}
 assumed={isAssumed('allow_carryover_incremental')}
 onOpen={() => setDCarry(P.allow_carryover_incremental ? P.max_incremental_in_focus : 0)}
 onSave={() => saveSchedulingPreferences({
 allow_carryover_incremental: dCarry > 0,
 max_incremental_in_focus: Math.max(dCarry, 1),
 })}
 testId="set-carryover"
 >
 <select value={dCarry} onChange={e => setDCarry(Number(e.target.value))} className={fieldCls}>
 <option value={0}>No, start fresh</option>
 {[1,2,3,4,5].map(n => <option key={n} value={n}>Up to {countWord(n,'thing')}</option>)}
 </select>
 </SettingRow>
 </SettingGroup>

 <SettingGroup
 title="What Today shows"
 blurb="What lands on the Today list, and what is allowed to count against your limit."
 >
 <SettingSwitch
 label="Top the list back up"
 because="Finish one thing and the next best one takes its place."
 checked={P.auto_backfill_focus}
 onChange={v => saveSchedulingPreferences({ auto_backfill_focus: v })}
 testId="set-backfill"
 />
 <SettingSwitch
 label="Show habits and routines"
 because="Anything that repeats appears on Today alongside your tasks."
 checked={P.show_recurring_in_today}
 onChange={v => saveSchedulingPreferences({ show_recurring_in_today: v })}
 testId="set-show-recurring"
 />
 <SettingSwitch
 label="Habits count toward the limit"
 because="Off means they sit apart and never eat into your things-a-day."
 checked={P.recurring_counts_toward_focus}
 onChange={v => saveSchedulingPreferences({ recurring_counts_toward_focus: v })}
 testId="set-recurring-counts"
 />
 <SettingSwitch
 label="Ease off on harder days"
 because="Lowers the limit when momentum is low, so a hard day stays finishable."
 checked={P.enable_low_momentum_mode}
 onChange={v => saveSchedulingPreferences({ enable_low_momentum_mode: v })}
 testId="set-low-momentum"
 />
 </SettingGroup>

 <SettingGroup
 title="When you are at your best"
 blurb="1 is running on empty, 5 is your sharpest. Harder things get placed in your stronger hours."
 >
 <div className="space-y-2 pt-1">
 {TIME_PERIODS.map(period => (
 <div key={period.key} className="flex items-center gap-3">
 <span className="w-28 shrink-0 text-app-micro text-secondary">{period.label}</span>
 <div className="flex gap-1">
 {[1,2,3,4,5].map(level => (
 <button key={level} type="button"
 aria-label={`${period.label}: ${level} of 5`}
 onClick={() => {
 const next = { ...P.energy_levels, [period.key]: level }
 saveSchedulingPreferences({ energy_levels: next })
 }}
 className={`h-8 w-8 rounded-lg border text-app-micro ${
 (P.energy_levels[period.key] || 0) >= level
 ?'border-brand bg-brand-subtle text-brand'
 :'border-soft text-muted hover:bg-surface-alt'}`}>
 {level}
 </button>
 ))}
 </div>
 </div>
 ))}
 </div>
 </SettingGroup>

 <SettingGroup
 title="Why any of this matters"
 blurb="The reason underneath the plan."
 >
 <SettingRow
 label="What this is all for"
 because="A sentence about why, not another goal. It travels with your week when Ori works out what to suggest."
 value={P.primary_motivation?.trim()}
 width="w-96"
 onOpen={() => setDWhy(P.primary_motivation ||'')}
 onSave={save({ primary_motivation: dWhy })}
 testId="set-why"
 >
 <textarea rows={4} value={dWhy} onChange={e => setDWhy(e.target.value)}
 placeholder="e.g. I want to be strong enough to keep up with my kids."
 className={`${fieldCls} resize-none`} />
 </SettingRow>
 </SettingGroup>
 </div>
 )
 }

 // ─────────────────────────────────────────────────────────────────────────
 // RENDER SECURITY TAB
 // ─────────────────────────────────────────────────────────────────────────
 
 const renderSecuritySettings = () => {
 if (securityLoading) {
 return (
 <div className="flex items-center justify-center h-64">
 <Loader2 className="h-8 w-8 animate-spin text-brand" />
 </div>
 )
 }

 const stats = securityData?.stats
 const sessions = securityData?.sessions || []

 return (
 <div className="space-y-6">
 {/* 2FA Status Card - Most Important */}
  <SettingGroup
 tone={mfaStatus?.mfa_enabled ? 'good' : 'bad'}
 title={
 <span className="flex items-center gap-2">
 {mfaStatus?.mfa_enabled
 ? <><ShieldCheck className="h-4 w-4 text-ok" /><span className="text-ok">Two-step sign-in is on</span></>
 : <><ShieldAlert className="h-4 w-4 text-bad" /><span className="text-bad">Two-step sign-in is needed</span></>}
 </span>
 }
 blurb={mfaStatus?.mfa_enabled
 ? 'Your account is protected by an authenticator app.'
 : 'It protects your account and your personal data. Please turn it on.'}
 >
 {mfaStatus?.mfa_enabled ? (
 <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-ok">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Your account is secured</span>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/mfa-setup')}
                  className="border-ok text-ok hover:bg-ok/20"
 >
 <Shield className="h-4 w-4 mr-2" />
 Manage 2FA
 </Button>
 </div>
 ) : (
 <div className="space-y-4">
 <div className="bg-surface rounded-lg p-4 border border-bad/30">
 <h4 className="font-semibold text-primary mb-2">Why is 2FA required?</h4>
 <ul className="space-y-2 text-sm text-secondary">
 <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-ok mt-0.5 flex-shrink-0" />
                    <span>Protects against unauthorized access even if your password is compromised</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-ok mt-0.5 flex-shrink-0" />
                    <span>Secures your dreams, goals, and personal data from intruders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-ok mt-0.5 flex-shrink-0" />
 <span>Takes less than 2 minutes to set up with Google Authenticator</span>
 </li>
 </ul>
 </div>
 <Button 
 onClick={() => navigate('/mfa-setup')}
                  className="w-full bg-bad hover:opacity-90 text-on-brand font-semibold py-3 text-lg shadow-lg hover:shadow-xl transition-all"
 >
 <Shield className="h-5 w-5 mr-2" />
 Enable Two-Factor Authentication Now
 </Button>
 </div>
 )}
 </SettingGroup>

 {/* Security Overview Stats */}
  <SettingGroup
 title="Sign-in activity"
 action={
 <div className="flex items-center gap-2">
 <select
 value={securityDays}
 onChange={(e) => { setSecurityDays(parseInt(e.target.value)); loadSecurityData() }}
 className="bg-surface-alt border border-soft rounded-lg px-3 py-1.5 text-sm"
 >
 <option value={7}>Last 7 days</option>
 <option value={30}>Last 30 days</option>
 <option value={90}>Last 90 days</option>
 </select>
 <Button variant="outline" size="sm" onClick={loadSecurityData}>
 <RefreshCw className="h-4 w-4" />
 </Button>
 </div>
 }
 >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-brand-subtle rounded-lg">
                  <div className="text-xl font-semibold text-brand">{stats?.total_logins || 0}</div>
                  <div className="text-xs text-secondary">Total Logins</div>
                </div>
                <div className="text-center p-4 bg-ok/10 rounded-lg">
                  <div className="text-xl font-semibold text-ok">{stats?.success_rate || 0}%</div>
                  <div className="text-xs text-secondary">Success Rate</div>
                </div>
                <div className="text-center p-4 bg-brand-subtle rounded-lg">
                  <div className="text-xl font-semibold text-brand">{stats?.unique_devices || 0}</div>
                  <div className="text-xs text-secondary">Unique Devices</div>
                </div>
                <div className="text-center p-4 bg-bad/10 rounded-lg">
                  <div className="text-xl font-semibold text-bad">{stats?.high_risk_logins || 0}</div>
 <div className="text-xs text-secondary">High Risk</div>
 </div>
 </div>
 </SettingGroup>

 {/* Active Sessions */}
  <SettingGroup
 title={`Devices signed in (${sessions.length})`}
 blurb="Anything here can open your account."
 action={sessions.length > 1 ? (
 <Button variant="outline" size="sm" onClick={handleTerminateAllSessions} className="text-bad border-bad hover:bg-bad/10">
 <LogOut className="h-4 w-4 mr-2" />
 Sign out the others
 </Button>
 ) : undefined}
 >
 <div className="space-y-3">
 {sessions.length > 0 ? sessions.map((session) => (
 <div key={session.id} className="flex items-center justify-between p-4 bg-surface-alt rounded-lg">
 <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${session.is_active ?'bg-ok/20' :'bg-surface-alt'}`}>
                    {session.device_type?.toLowerCase() ==='mobile' ? (
                      <Smartphone className={`h-5 w-5 ${session.is_active ?'text-ok' :'text-muted'}`} />
                    ) : session.device_type?.toLowerCase() ==='tablet' ? (
                      <Tablet className={`h-5 w-5 ${session.is_active ?'text-ok' :'text-muted'}`} />
                    ) : (
                      <Monitor className={`h-5 w-5 ${session.is_active ?'text-ok' :'text-muted'}`} />
                    )}
 </div>
 <div>
 <div className="flex items-center gap-2">
 <span className="font-medium text-primary">
 {session.device ||'Unknown Device'}
 </span>
 {session.is_active && (
                      <span className="px-2 py-0.5 bg-ok/20 text-ok rounded-full text-xs">
 Current
 </span>
 )}
 {session.two_factor_used && (
                      <span className="px-2 py-0.5 bg-brand-subtle text-brand rounded-full text-xs flex items-center gap-1">
 <Key className="w-3 h-3" />
 2FA
 </span>
 )}
 </div>
 <div className="text-sm text-muted">
 {session.location ||'Unknown Location'} • {session.browser_name} • {session.os_name}
 </div>
 {session.ip_address && (
 <div className="text-xs text-muted font-mono">
 IP: {session.ip_address}
 </div>
 )}
 <div className="text-xs text-muted">
 {session.created_at ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true }) :'Unknown'}
 </div>
 </div>
 </div>
 {!session.is_active && (
 <Button
 variant="outline"
 size="sm"
 onClick={() => handleTerminateSession(session.id)}
                    className="text-bad border-bad hover:bg-bad/10"
                  >
                    <LogOut className="h-4 w-4" />
 </Button>
 )}
 </div>
 )) : (
 <p className="text-center text-muted py-8">No active sessions</p>
 )}
 </div>
 </SettingGroup>

 {/* Change Password */}
 <SettingGroup title="Password and account" blurb="Your password and how you sign in.">
 {/* Authentication Method Info */}
 {securityData?.user?.oauth_provider && securityData.user.oauth_provider !=='local' && (
              <div className="p-4 bg-brand-subtle border border-brand rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-subtle rounded-full flex items-center justify-center">
 <ShieldCheck className="h-5 w-5 text-brand" />
 </div>
 <div className="flex-1">
                  <div className="font-medium text-brand">
                      {securityData.user.oauth_provider.charAt(0).toUpperCase() + securityData.user.oauth_provider.slice(1)} Sign-In
                    </div>
                    <div className="text-sm text-brand">
 You're signed in with {securityData.user.oauth_provider.charAt(0).toUpperCase() + securityData.user.oauth_provider.slice(1)}. 
 Your password is managed by your {securityData.user.oauth_provider.charAt(0).toUpperCase() + securityData.user.oauth_provider.slice(1)} account.
 </div>
 </div>
 </div>
 </div>
 )}
 
 <div className="flex items-center justify-between p-4 bg-surface-alt rounded-lg">
 <div>
 <div className="font-medium text-primary">Change Password</div>
 <div className="text-sm text-muted">
 {securityData?.user?.oauth_provider && securityData.user.oauth_provider !=='local'
 ? `Manage your password through ${securityData.user.oauth_provider.charAt(0).toUpperCase() + securityData.user.oauth_provider.slice(1)}`
 :'Update your password regularly for better security'
 }
 </div>
 </div>
 {securityData?.user?.oauth_provider && securityData.user.oauth_provider !=='local' ? (
 <Button 
 variant="outline" 
 onClick={() => {
 // Open the appropriate OAuth provider's account settings
 const providerUrls: Record<string, string> = {
'google':'https://myaccount.google.com/security',
'github':'https://github.com/settings/security',
'microsoft':'https://account.microsoft.com/security',
'apple':'https://appleid.apple.com/account/manage'
 }
 const url = providerUrls[securityData.user.oauth_provider?.toLowerCase() ?? ''] ||'#'
 if (url !=='#') {
 window.open(url,'_blank','noopener,noreferrer')
 }
 }}
 >
 <Lock className="h-4 w-4 mr-2" />
 Manage on {securityData.user.oauth_provider.charAt(0).toUpperCase() + securityData.user.oauth_provider.slice(1)}
 </Button>
 ) : (
 <Button variant="outline" onClick={() => navigate('/change-password')}>
 Change Password
 </Button>
 )}
 </div>
 <div className="flex items-center justify-between p-4 bg-surface-alt rounded-lg">
 <div>
 <div className="font-medium text-primary flex items-center gap-2">
 Email Verification
                    {securityData?.user?.email_verified ? (
                      <CheckCircle className="h-4 w-4 text-ok" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-warn" />
                    )}
 </div>
 <div className="text-sm text-muted">
 {securityData?.user?.email_verified ?'Your email is verified' :'Please verify your email address'}
 </div>
 </div>
 {!securityData?.user?.email_verified && (
 <Button 
 variant="outline" 
 onClick={handleResendVerification}
 disabled={resendingVerification}
 >
 {resendingVerification ? (
 <>
 <Loader2 className="h-4 w-4 mr-2 animate-spin" />
 Sending...
 </>
 ) : (
'Resend Verification'
 )}
 </Button>
 )}
 </div>
 {verificationMessage && (
 <div className={`mt-4 p-3 rounded-lg text-sm ${
 verificationMessage.type ==='success' 
                  ?'bg-ok/10 text-ok' 
                  :'bg-bad/10 text-bad'
 }`}>
 {verificationMessage.text}
 </div>
 )}
 </SettingGroup>
 </div>
 )
 }

 return (
 <div className="space-y-6">
<div>
 <h1 className="text-xl font-semibold flex items-center">
 <User className="h-8 w-8 text-brand mr-3" />
 Settings
 </h1>
 <p className="text-muted mt-2">
 What {BRAND.name} knows about you, and how it fits around your life.
 </p>
 </div>

 {/* Four rooms, each named for what it decides. The old five carried
     nested sub-tabs (Preferences → Appearance/Scheduling → four more),
     so a setting could be three clicks deep with no way to guess which
     branch held it. */}
 <div className="flex gap-1 md:gap-2 border-b border-soft overflow-x-auto pb-px">
 {TABS.map(t => (
 <button
 key={t.id}
 onClick={() => setActiveTab(t.id)}
 className={`flex items-center gap-2 px-4 md:px-6 py-3 border-b-2 transition-all whitespace-nowrap ${
 activeTab === t.id
 ? 'border-brand text-brand font-medium bg-brand-subtle'
 : 'border-transparent text-muted hover:text-secondary hover:bg-surface-alt'
 }`}
 >
 <t.icon className="h-5 w-5" />
 <span>{t.label}</span>
 </button>
 ))}
 </div>

 {/* Floating Toast Notification */}
 {saveMessage && (
 <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className={`p-4 rounded-xl shadow-2xl flex items-center gap-3 w-full sm:min-w-[300px] sm:max-w-[450px] ${
            saveMessage.type ==='success' 
              ?'bg-ok text-on-brand' 
              :'bg-bad text-on-brand'
          }`}>
            <div className={`p-2 rounded-full ${
              saveMessage.type ==='success' ?'bg-ok' :'bg-bad'
            }`}>
 {saveMessage.type ==='success' ? (
 <CheckCircle className="h-5 w-5" />
 ) : (
 <AlertCircle className="h-5 w-5" />
 )}
 </div>
 <div className="flex-1">
 <p className="font-semibold text-sm">
 {saveMessage.type ==='success' ?'Success!' :'Error'}
 </p>
 <p className="text-sm opacity-90">{saveMessage.text}</p>
 </div>
 <button 
 onClick={() => setSaveMessage(null)}
 className="p-1 hover:bg-surface/20 rounded-full transition-colors"
 >
 <span className="text-xl leading-none">×</span>
 </button>
 </div>
 </div>
 )}

 {/* Tab Content */}
 
{/* You — who you are, what the coach learned, and the look. */}
 {activeTab === 'profile' && (
 <div className="space-y-6">
 {renderPersonalInfo()}
 {renderLook()}
 </div>
 )}

 {/* Your day — the shape of a day, and how much of it to fill. */}
 {activeTab === 'day' && (
 <div className="space-y-4">
 {/* Once for the whole tab. It was on each group, which put it twice on
     one screen — the same repetition the per-row "assumed" caption had. */}
 <p className="px-1 text-app-micro text-muted">{ASSUMED_NOTE}</p>
 {/* The same questions, asked one at a time. A long form is the worst
     way to meet these for the first time — which is why not one profile
     in production had ever filled this page in. */}
 <button
 type="button"
 onClick={() => navigate('/welcome')}
 className="w-full flex items-center justify-between rounded-2xl border border-soft bg-surface-alt px-5 py-4 text-left hover:bg-surface transition-colors"
 >
 <div>
 <div className="text-sm font-semibold text-primary">Walk me through it instead</div>
 <div className="text-xs text-secondary mt-0.5">Five short questions, one at a time. Nothing here is lost.</div>
 </div>
 <span className="text-brand text-sm font-semibold shrink-0">Start →</span>
 </button>
 {renderSchedulingPreferences()}
 {/* LifeMap importance budgets. Renders only when the
     prioritization engine is enabled (returns null otherwise). */}
 <LifeAreaBudgetsEditor />
 </div>
 )}

 {/* Reaching you — every way the app speaks first, in one place.
     Until 2026-09-06 reminder insistence lived under Preferences →
     Appearance while check-ins were a separate top-level tab, so the
     two halves of one decision were never on screen together. */}
 {activeTab === 'attention' && (
 <div className="space-y-6">
 {renderAttention()}
 <WithYouSettings embedded />
 <button
 type="button"
 onClick={() => navigate('/how-horton-helps')}
 className="w-full flex items-center justify-between rounded-2xl border border-brand-subtle-border bg-brand-subtle/40 px-5 py-4 text-left hover:bg-brand-subtle transition-colors"
 >
 <div>
 <div className="text-sm font-bold text-primary">How {BRAND.assistant} helps me</div>
 <div className="text-xs text-secondary mt-0.5">Tune how proactive it is — nudges, offers, and quiet hours</div>
 </div>
 <span className="text-brand text-sm font-semibold shrink-0">Open →</span>
 </button>
 </div>
 )}

 {/* Security Tab */}
 {activeTab ==='security' && (
 <>
 {renderSecuritySettings()}

 {/* Developer Tools Section */}
  <SettingGroup
 tone="warn"
 title={
 <button type="button" onClick={toggleDevTools} className="flex items-center gap-2 text-warn">
 <Wrench className="h-4 w-4" />
 Developer tools
 {showDevTools ? <ChevronDown className="h-4 w-4 text-muted" /> : <ChevronRight className="h-4 w-4 text-muted" />}
 </button>
 }
 blurb="Utilities for testing. They change real data."
 >
 {showDevTools && (
 <div className="space-y-4">
 {/* Warning Banner */}
 <div className="flex items-start gap-3 p-4 bg-bad/10 border border-bad/30 rounded-lg">
 <AlertTriangle className="h-5 w-5 text-bad flex-shrink-0 mt-0.5" />
 <div>
                    <p className="font-medium text-bad">Warning</p>
                      <p className="text-sm text-bad">
 These tools can permanently delete data. Use with caution.
 </p>
 </div>
 </div>

 {/* Item Counts */}
 {devToolsLoading ? (
 <div className="flex items-center justify-center py-6">
 <RefreshCw className="h-5 w-5 animate-spin text-brand mr-2" />
 <span className="text-secondary">Loading...</span>
 </div>
 ) : itemCounts && (
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <Database className="h-5 w-5 text-brand" />
 <span className="font-medium text-primary">Your Hierarchy Items</span>
 </div>
 <Button variant="outline" size="sm" onClick={loadItemCounts}>
 <RefreshCw className={`h-4 w-4 mr-1 ${devToolsLoading ?'animate-spin' :''}`} />
 Refresh
 </Button>
 </div>
 
                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-brand-subtle rounded-lg">
                      <div className="text-xl font-semibold text-brand">{itemCounts.dreams}</div>
                      <div className="text-xs text-secondary">Dreams</div>
                    </div>
                    <div className="text-center p-3 bg-brand-subtle rounded-lg">
                      <div className="text-xl font-semibold text-brand">{itemCounts.aspirations}</div>
                      <div className="text-xs text-secondary">Aspirations</div>
                    </div>
                    <div className="text-center p-3 bg-ok/10 rounded-lg">
                      <div className="text-xl font-semibold text-ok">{itemCounts.goals}</div>
                      <div className="text-xs text-secondary">Goals</div>
                    </div>
                    <div className="text-center p-3 bg-brand-subtle rounded-lg">
                      <div className="text-xl font-semibold text-brand">{itemCounts.tasks}</div>
                      <div className="text-xs text-secondary">Tasks</div>
                    </div>
                    <div className="text-center p-3 bg-brand-subtle rounded-lg">
                      <div className="text-xl font-semibold text-brand">{itemCounts.action_items}</div>
                      <div className="text-xs text-secondary">Actions</div>
                    </div>
                    <div className="text-center p-3 bg-warn/10 rounded-lg">
                      <div className="text-xl font-semibold text-warn">{itemCounts.roadblocks}</div>
                      <div className="text-xs text-secondary">Roadblocks</div>
                    </div>
                    <div className="text-center p-3 bg-brand-subtle rounded-lg">
                      <div className="text-xl font-semibold text-brand">{itemCounts.knowledge_skills}</div>
                      <div className="text-xs text-secondary">Skills</div>
                    </div>
 <div className="text-center p-3 bg-surface-alt rounded-lg border-2 border-soft">
 <div className="text-xl font-semibold text-primary">{totalItems}</div>
 <div className="text-xs text-secondary font-semibold">Total</div>
 </div>
 </div>
 </div>
 )}

 {/* Clear Result */}
 {clearResult && (
 <div className="p-4 bg-ok/10 border border-ok/30 rounded-lg">
                  <p className="font-medium text-ok mb-2">
                    ✅ Cleared {clearResult.total_deleted} items
                  </p>
                  <div className="text-sm text-ok break-words">
 {Object.entries(clearResult.deleted_counts).map(([type, count]) => (
 <span key={type} className="mr-3 inline-block max-w-full align-top">
 {type.replace(/_/g, ' ')}: {formatDeletedCountFragment(count)}
 </span>
 ))}
 </div>
 </div>
 )}

 {/* Clear All Button */}
 <div className="pt-4 border-t border-soft">
 {!showClearConfirm ? (
 <Button
 variant="destructive"
 onClick={() => setShowClearConfirm(true)}
 disabled={totalItems === 0}
 >
 <Trash2 className="h-4 w-4 mr-2" />
 Clear All Items ({totalItems} total)
 </Button>
 ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-warn/10 border border-warn rounded-lg">
                        <p className="font-semibold text-warn mb-1">
                          ⚠️ Are you absolutely sure?
                        </p>
                        <p className="text-sm text-warn">
 This will permanently delete <strong>{totalItems} items</strong>. This cannot be undone.
 </p>
 </div>
 <div className="flex gap-2">
 <Button
 variant="destructive"
 onClick={handleClearAllItems}
 disabled={clearLoading}
 >
 {clearLoading ? (
 <>
 <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
 Deleting...
 </>
 ) : (
 <>
 <Trash2 className="h-4 w-4 mr-2" />
 Yes, Delete Everything
 </>
 )}
 </Button>
 <Button
 variant="outline"
 onClick={() => setShowClearConfirm(false)}
 disabled={clearLoading}
 >
 Cancel
 </Button>
 </div>
 </div>
 )}
 </div>
  </div>
  )}
 </SettingGroup>
 </>
 )}
 </div>
 )
}



/** Reminder attention policy (F5 §10 + owner ruling 2026-08-25):
 *  persistence default + what critical reminders do during quiet hours.
 *  Saves immediately (its own endpoint, separate from app-prefs). */
function ReminderAttentionSettings() {
  const [level, setLevel] = useState<string>('persistent')
  const [quietMode, setQuietMode] = useState<string>('respect')
  const [ackCopy, setAckCopy] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void getReminderSettings().then((s) => {
      if (s) {
        setLevel(s.default_persistence)
        setQuietMode(s.critical_quiet_mode)
      }
    })
  }, [])

  const chooseLevel = async (lvl: string, acknowledge = false) => {
    setSaving(true)
    try {
      const r = await setReminderDefault(lvl, acknowledge)
      if (r?.needs_acknowledgement) { setAckCopy(r.copy || '') ; return }
      if (r?.success) { setLevel(lvl); setAckCopy(null) }
    } finally { setSaving(false) }
  }

  return (
    // Two rows, bordered like every other setting, so the labels read as
    // settings rather than as sections competing with the group heading.
    <div>
      <div className="border-b border-soft pb-3">
        <label className="text-app-body text-primary mb-1 block">Reminder persistence</label>
        <p className="text-app-micro text-muted mb-2">
          How new reminders behave by default — each reminder can override this.
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { v: 'normal', label: 'Normal', hint: 'notify once' },
            { v: 'persistent', label: 'Persistent', hint: 'remind until resolved' },
            { v: 'aggressive', label: 'Aggressive', hint: 'frequent + escalating' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              disabled={saving}
              onClick={() => void chooseLevel(o.v)}
              className={`min-h-touch rounded-xl border-2 px-4 text-left transition-all ${
                level === o.v
                  ? 'border-brand-subtle bg-brand-subtle text-brand'
                  : 'border-soft text-secondary hover:border-brand'
              }`}
            >
              <span className="block text-sm font-medium">{o.label}</span>
              <span className="block text-xs text-muted">{o.hint}</span>
            </button>
          ))}
        </div>
        {ackCopy && (
          <div className="mt-2 rounded-xl border border-warn/40 bg-warn/10 p-3">
            <p className="text-xs text-secondary">{ackCopy}</p>
            <button
              type="button"
              disabled={saving}
              onClick={() => void chooseLevel('aggressive', true)}
              className="mt-2 min-h-10 rounded-lg bg-warn px-4 text-xs font-bold text-white hover:opacity-90"
            >
              Use Aggressive
            </button>
          </div>
        )}
      </div>

      <div className="pt-3">
        <label className="text-app-body text-primary mb-1 block">Critical reminders during quiet hours</label>
        <p className="text-app-micro text-muted mb-2">
          When a critical (all-hands) reminder comes due while you sleep, {BRAND.assistant} either
          holds it — and tells you it's holding it — or wakes you.
        </p>
        <div className="space-y-2">
          {[
            { v: 'respect', label: 'Honor my quiet hours', d: "Held silently until you wake; you'll see it was held, and any reminder offers a 'keep alerting me' override." },
            { v: 'sovereign', label: 'Wake me anyway', d: 'A critical reminder fires regardless of quiet hours.' },
          ].map((o) => (
            <label
              key={o.v}
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                quietMode === o.v ? 'border-brand-subtle bg-brand-subtle' : 'border-soft hover:border-soft'
              }`}
            >
              <input
                type="radio"
                name="critical_quiet_mode"
                checked={quietMode === o.v}
                onChange={async () => { setQuietMode(o.v); await setCriticalQuietMode(o.v as 'respect' | 'sovereign') }}
                className="mt-1 accent-brand"
              />
              <div>
                <span className="font-medium text-primary">{o.label}</span>
                <p className="text-xs text-muted mt-0.5">{o.d}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

    </div>
  )
}
